import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Sparkles, TriangleAlert, Lock, X, Search } from 'lucide-react';
import PortalPopover from '../PortalPopover';
import { STANDARDS_GROUPS, findStandard } from '../../../utils/calculations';

// Per-row Norm picker. Drill-down popover with three navigation
// levels mirroring the old Toolbox standards menu (msimpex prefers
// hierarchy over a flat search list — it stays manageable as the
// standards catalog grows):
//
//   [Categories]   [Items in selected category]   [Sub-options of item]
//
// Search input at the top collapses navigation into a flat result
// list (still respecting Free-tier lock).
//
// Free users can only pick `iso2768m` (and Manuell); other norms
// surface a lock + Pro nudge so the affordance is visible but gated.

const FREE_ALLOWED = new Set(['', 'iso2768m']);

// Flatten the nested catalog into a search-friendly list. Final ids
// match what calculations.findStandard / the store consume directly.
function flatten() {
  const out = [{ id: '', groupName: '', name: 'Manuell', searchKey: 'manuell' }];
  for (const g of STANDARDS_GROUPS) {
    for (const item of g.items) {
      if (item.subOptions) {
        for (const s of item.subOptions) {
          out.push({
            id: `${item.id}_${s.id}`,
            groupName: g.name,
            itemName: item.name,
            name: `${item.name} ${s.name}`,
            searchKey: `${g.name} ${item.name} ${s.name}`.toLowerCase(),
          });
        }
      } else {
        out.push({
          id: item.id,
          groupName: g.name,
          itemName: item.name,
          name: item.name,
          searchKey: `${g.name} ${item.name}`.toLowerCase(),
        });
      }
    }
  }
  return out;
}

export default function NormCell({ value, overridden, outOfRange = false, onChange, isFree }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeGroupId, setActiveGroupId] = useState(STANDARDS_GROUPS[0]?.id ?? null);
  const [subItem, setSubItem] = useState(null);
  const ref = useRef(null);

  const all = useMemo(() => flatten(), []);
  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return all.filter((o) => o.searchKey.includes(q));
  }, [all, query]);

  const std = value ? findStandard(value) : null;
  // Use the compact `shortLabel` for the cell so it stays readable in
  // the table (e.g. "DIN 16742 TG3 (W)" rather than "DIN 16742 / ISO
  // 20457 TG 3 (Werkzeuggebunden (W))"). Hover-Tooltip below still
  // shows the long form for full context.
  const fullDisplay  = std ? `${std.groupName} ${std.name}` : (value ? value : 'Manuell');
  const shortDisplay = std?.shortLabel ?? fullDisplay;

  const activeGroup = STANDARDS_GROUPS.find((g) => g.id === activeGroupId) || null;

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setSubItem(null);
    setQuery('');
  };

  return (
    <>
      <button
        ref={ref}
        data-tour="norm-cell"
        type="button"
        tabIndex={-1}
        onClick={() => {
          setOpen((o) => !o);
          setQuery('');
          setSubItem(null);
          // Pre-select the group containing the active norm so the
          // popover opens on the right pane every time. findStandard
          // returns the matching item, then we walk STANDARDS_GROUPS
          // to find its owning group id.
          if (value) {
            for (const g of STANDARDS_GROUPS) {
              const hit = g.items.find((it) => it.id === value || (it.subOptions && value.startsWith(`${it.id}_`)));
              if (hit) { setActiveGroupId(g.id); break; }
            }
          }
        }}
        title={outOfRange
          ? `${fullDisplay}: Nennmaß außerhalb des Tabellenbereichs dieser Norm. Toleranz bitte von Hand eintragen.`
          : (overridden ? `Norm ${fullDisplay} - manuell überschrieben` : fullDisplay)}
        className={`w-full text-left border ${
          value && outOfRange
            ? 'bg-warn/10 border-warn/30 text-warn font-semibold'
            : value
              ? (overridden ? 'border-line bg-surface-2 text-ink-3' : 'border-line bg-brand-soft text-brand font-semibold')
              : 'border-line bg-surface text-ink-3'
        }`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '0 8px', height: 26,
          borderRadius: 5, cursor: 'pointer',
          fontSize: 11,
        }}
      >
        {value && (outOfRange
          ? <TriangleAlert size={11} aria-hidden="true" />
          : <Sparkles size={11} aria-hidden="true" />)}
        <span className="truncate flex-1">{shortDisplay}</span>
        <ChevronDown size={11} aria-hidden="true" />
      </button>
      <PortalPopover anchorRef={ref} open={open} onClose={() => setOpen(false)} minWidth={500}>
        {/* Search bar — always present. Non-empty query collapses the
            drill-down into a flat list so power users can jump straight
            to a known norm without navigating categories. */}
        <div style={{ padding: 8, borderBottom: '1px solid var(--color-line)' }}>
          <div
            className="bg-surface-2 border border-line"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 5 }}
          >
            <Search size={12} className="text-ink-3" aria-hidden="true" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Norm suchen…"
              className="text-ink"
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontSize: 12,
              }}
              aria-label="Norm suchen"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-ink-3 hover:text-ink-2"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                aria-label="Suche leeren"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {searchHits ? (
          // ── Flat search results ───────────────────────────────────
          <div role="listbox" style={{ maxHeight: '50vh', overflowY: 'auto', padding: 4 }}>
            {searchHits.length === 0 && (
              <div className="text-ink-4" style={{ padding: '8px 10px', fontSize: 11 }}>
                Keine Treffer
              </div>
            )}
            {searchHits.map((o) => {
              const active = value === o.id;
              const locked = isFree && !FREE_ALLOWED.has(o.id);
              return (
                <button
                  key={o.id || 'manual'}
                  role="option"
                  aria-selected={active}
                  type="button"
                  onClick={() => !locked && pick(o.id)}
                  disabled={locked}
                  className={`w-full text-left ${
                    active ? 'bg-brand-soft text-brand font-semibold' : 'text-ink-2 hover:bg-surface-2'
                  } ${locked ? 'opacity-60' : ''}`}
                  style={{
                    border: 'none', padding: '6px 8px', borderRadius: 4,
                    fontSize: 12, cursor: locked ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {o.groupName && (
                    <span className="text-ink-4" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', minWidth: 90 }}>
                      {o.groupName}
                    </span>
                  )}
                  <span className="flex-1">{o.name}</span>
                  {locked && <Lock size={10} className="text-ink-4" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        ) : (
          // ── Two-pane drill-down ───────────────────────────────────
          <div role="group" style={{ display: 'flex', maxHeight: '50vh' }}>
            {/* Left rail: categories + Manuell pill on top */}
            <div
              className="bg-surface-2 border-r border-line"
              style={{ width: 200, flexShrink: 0, overflowY: 'auto', padding: 4 }}
            >
              <NavButton
                label="Manuell"
                active={value === ''}
                onClick={() => pick('')}
                small
              />
              <div className="text-ink-4" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '8px 8px 4px' }}>
                Kategorien
              </div>
              {STANDARDS_GROUPS.map((g) => (
                <NavButton
                  key={g.id}
                  label={g.name}
                  active={activeGroupId === g.id}
                  onClick={() => { setActiveGroupId(g.id); setSubItem(null); }}
                  trailing={<ChevronRight size={11} aria-hidden="true" className={activeGroupId === g.id ? 'opacity-100' : 'opacity-40'} />}
                />
              ))}
            </div>

            {/* Right pane: items in selected group, or sub-options */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 4, minWidth: 260 }}>
              {!activeGroup ? (
                <div className="text-ink-4" style={{ padding: '12px 10px', fontSize: 11 }}>
                  Kategorie wählen
                </div>
              ) : subItem ? (
                <>
                  <button
                    type="button"
                    onClick={() => setSubItem(null)}
                    className="text-brand hover:bg-brand-soft"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 6px', border: 'none', background: 'transparent',
                      borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                      letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4,
                    }}
                  >
                    <ChevronLeft size={11} aria-hidden="true" /> Zurück
                  </button>
                  <div className="text-ink-4" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '0 8px 6px' }}>
                    {subItem.name}
                  </div>
                  {subItem.subOptions.map((s) => {
                    const id = `${subItem.id}_${s.id}`;
                    const active = value === id;
                    const locked = isFree && !FREE_ALLOWED.has(id);
                    return (
                      <NavButton
                        key={id}
                        label={s.name}
                        active={active}
                        locked={locked}
                        onClick={() => !locked && pick(id)}
                      />
                    );
                  })}
                </>
              ) : (
                activeGroup.items.map((item) => {
                  const id = item.id;
                  const hasSub = !!item.subOptions;
                  const active = !hasSub && value === id;
                  const locked = !hasSub && isFree && !FREE_ALLOWED.has(id);
                  return (
                    <NavButton
                      key={id}
                      label={item.name}
                      active={active}
                      locked={locked}
                      onClick={() => {
                        if (hasSub) setSubItem(item);
                        else if (!locked) pick(id);
                      }}
                      trailing={hasSub ? <ChevronRight size={11} aria-hidden="true" className="opacity-50" /> : null}
                    />
                  );
                })
              )}
            </div>
          </div>
        )}
      </PortalPopover>
    </>
  );
}

// Reusable nav row — used for categories, items, and sub-options so
// the three panes share the same visual rhythm + hit-target.
function NavButton({ label, active, locked, onClick, trailing, small }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      className={`w-full text-left ${
        active
          ? 'bg-brand-soft text-brand font-semibold'
          : locked
            ? 'text-ink-4 opacity-70'
            : 'text-ink-2 hover:bg-surface-2'
      }`}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: small ? '5px 8px' : '6px 8px',
        border: 'none', borderRadius: 4,
        cursor: locked ? 'not-allowed' : 'pointer',
        fontSize: 12, textAlign: 'left',
      }}
    >
      {/* Use pre-line so item names with embedded \n (e.g. "Rundheit /
          Parallelität / Rechtwinkligkeit / Symmetrie (GCT)") wrap on
          their authored line breaks instead of truncating. */}
      <span style={{ flex: 1, whiteSpace: 'pre-line', lineHeight: 1.25 }}>{label}</span>
      {locked && <Lock size={10} className="text-ink-4" aria-hidden="true" />}
      {trailing}
    </button>
  );
}
